/**
 * /dashboard/accounting/reports/trial-balance — Trial Balance (G_ACC8).
 *
 * As-of date picker + table + CSV download. Renders the §128(1) balance
 * proof (totals must match) and surfaces each account row with its
 * natural-balance side.
 *
 * Migration note (Phase 3e — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/accounting/reports/trial-balance/page.tsx.
 */

import { useState } from "react";
import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft, Download } from "lucide-react";
import { AccountingModuleShell } from "@/components/shared/accounting-module-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatPaise } from "@/lib/expense-format";
import { trialBalanceCSVURL } from "@/lib/api/accounting";
import { useTrialBalanceReport } from "@/hooks/use-accounting";
import { toApiError } from "@/lib/api";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/accounting/reports/trial-balance",
)({
  component: TrialBalanceRoute,
});

function todayUTCDate(): string {
  const d = new Date();
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function TrialBalanceRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const [asOf, setAsOf] = useState<string>(todayUTCDate());
  const [pending, setPending] = useState<string>(asOf);
  const query = useTrialBalanceReport(asOf);
  const data = query.data ?? null;
  const loading = query.isPending;
  const error = query.isError ? toApiError(query.error).message : null;

  return (
    <AccountingModuleShell
      title="Trial Balance"
      description="Companies Act §128(1) — debits and credits must agree."
      actions={
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" className="gap-1.5">
            <Link to="/app/$tenantSlug/accounting/reports" params={{ tenantSlug }}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </Button>
          <Button asChild className="gap-1.5">
            <a href={trialBalanceCSVURL(asOf)} download>
              <Download className="h-4 w-4" />
              Export CSV
            </a>
          </Button>
        </div>
      }
    >
      <Card>
        <CardContent className="grid gap-3 py-4 sm:grid-cols-[1fr_auto]">
          <div className="grid max-w-xs gap-1.5">
            <Label htmlFor="tb-asof">As of</Label>
            <Input
              id="tb-asof"
              type="date"
              value={pending}
              onChange={(e) => setPending(e.target.value)}
            />
          </div>
          <div className="flex items-end">
            <Button
              type="button"
              onClick={() => setAsOf(pending)}
              disabled={!pending || pending === asOf}
            >
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive" className="mt-4">
          <AlertTitle>Could not load trial balance</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="mt-4 space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-10" />
          ))}
        </div>
      ) : data ? (
        <div className="mt-4 space-y-4">
          {!data.balances_to_zero && (
            <Alert variant="destructive">
              <AlertTitle>Trial Balance does not balance</AlertTitle>
              <AlertDescription>
                Debits {formatPaise(data.total_debit_minor)} != Credits{" "}
                {formatPaise(data.total_credit_minor)}. This breaks the
                Companies Act §128(1) &ldquo;true and fair view&rdquo; assertion —
                investigate before filing.
              </AlertDescription>
            </Alert>
          )}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">Code</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead className="w-32">Type</TableHead>
                  <TableHead className="w-40 text-right">Debit</TableHead>
                  <TableHead className="w-40 text-right">Credit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="py-8 text-center text-sm text-muted-foreground"
                    >
                      No accounts have postings as of {data.as_of}.
                    </TableCell>
                  </TableRow>
                ) : (
                  data.rows.map((r) => (
                    <TableRow key={r.account_id}>
                      <TableCell className="font-mono text-xs">
                        {r.account_code}
                      </TableCell>
                      <TableCell>{r.account_name}</TableCell>
                      <TableCell className="capitalize text-muted-foreground">
                        {r.account_type}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.debit_minor === 0 ? "—" : formatPaise(r.debit_minor)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.credit_minor === 0
                          ? "—"
                          : formatPaise(r.credit_minor)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
                <TableRow className="border-t-2 font-semibold">
                  <TableCell colSpan={3}>TOTAL</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatPaise(data.total_debit_minor)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatPaise(data.total_credit_minor)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
          <p className="text-xs text-muted-foreground">
            {data.balances_to_zero ? "Balanced" : "Unbalanced"} as of{" "}
            {data.as_of}.
          </p>
        </div>
      ) : null}
    </AccountingModuleShell>
  );
}
